import { useState, useEffect } from 'react';
import { useGlobal } from '@/context/GlobalContext';
import { useAuth } from '@/context/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Building, Clock, User } from 'lucide-react';

const AdminSubmissions = () => {
  const { API } = useGlobal();
  const { token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSubmissions = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/contact-submissions`, { headers });
      setSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [API, token]);

  const updateStatus = async (id, status) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/contact-submissions/${id}/status?status=${status}`, {}, { headers });
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status } : s));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-green-100 text-green-700';
      case 'contacted': return 'bg-blue-100 text-blue-700';
      case 'closed': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div data-testid="admin-submissions">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 font-['Montserrat']">Contact Submissions</h1>
        <p className="text-slate-600">View and manage contact form submissions</p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-slate-100 animate-pulse h-32 rounded-sm" />
          ))}
        </div>
      ) : submissions.length === 0 ? (
        <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
          <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No submissions yet</h3>
          <p className="text-slate-500">Contact form submissions will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map(submission => (
            <div key={submission.id} className="bg-white rounded-sm border border-slate-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-semibold text-slate-900">{submission.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded ${getStatusColor(submission.status)}`}>
                      {submission.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Building className="w-3 h-3" />
                      {submission.company}
                    </span>
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {submission.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(submission.submitted_at)}
                    </span>
                  </div>
                </div>
                <Select 
                  value={submission.status} 
                  onValueChange={(value) => updateStatus(submission.id, value)}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="mb-4">
                <span className="inline-block text-xs font-medium text-[#5b3589] bg-purple-50 px-2 py-1 rounded">
                  {submission.project_type}
                </span>
              </div>

              <div className="bg-slate-50 p-4 rounded-sm">
                <p className="text-slate-700 text-sm whitespace-pre-wrap">{submission.message}</p>
              </div>

              <div className="mt-4 flex gap-4">
                <a 
                  href={`mailto:${submission.email}`}
                  className="text-sm text-[#5b3589] hover:underline"
                >
                  Reply via Email
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSubmissions;
